import { useState } from 'react';
import { Banknote } from 'lucide-react';
import { WithdrawalStatus, type WithdrawalRequest } from '@shared/ledger';

import { Button } from '@/components/ui/Button';
import { WithdrawalStatusBadge } from '@/components/ui/Badge';
import { CopyButton } from '@/components/ui/CopyButton';
import { EmptyState, InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useWithdrawals } from '@/lib/queries';
import { toMessage } from '@/lib/api';
import { absoluteDateTime, relativeTime, usdt } from '@/lib/format';
import { NOUN, plural } from '@/lib/plural';

/**
 * Every withdrawal the trader has asked for, and where each one got to.
 *
 * A trader opens this to answer one question — "where is my money" — so the
 * status word, the amount and the on-chain hash are the three things a row
 * leads with. The hash gets a copy control because the next thing that happens
 * after reading it is pasting it into a block explorer, and hand-copying 64
 * characters off a phone screen is not a thing anyone does correctly.
 */

const PAGE_SIZE = 10;

export function WithdrawalHistory() {
  const [limit, setLimit] = useState(PAGE_SIZE);
  const withdrawals = useWithdrawals(limit);

  if (withdrawals.isPending) {
    return (
      <div className="divide-y divide-line" role="status" aria-busy="true">
        <span className="sr-only">Загружаю историю выводов</span>
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="px-4 py-3.5">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="mt-2 h-3 w-48" />
          </div>
        ))}
      </div>
    );
  }

  if (withdrawals.isError) {
    return (
      <div className="p-4">
        <InlineError
          message={toMessage(withdrawals.error, 'Не удалось загрузить историю выводов.')}
          onRetry={() => void withdrawals.refetch()}
        />
      </div>
    );
  }

  const { items, total } = withdrawals.data;

  if (items.length === 0) {
    return (
      <EmptyState
        icon={Banknote}
        title="Выводов пока не было"
        description="Здесь появится каждая заявка на вывод: сумма, адрес, статус и хеш перевода, когда администратор его отправит."
      />
    );
  }

  const hasMore = items.length < total;

  return (
    <>
      <ul className="divide-y divide-line">
        {items.map((withdrawal) => (
          <WithdrawalRow key={withdrawal.id} withdrawal={withdrawal} />
        ))}
      </ul>

      {hasMore ? (
        <div className="border-t border-line px-4 py-3">
          <Button
            fullWidth
            onClick={() => setLimit((current) => current + PAGE_SIZE)}
            isLoading={withdrawals.isFetching}
            className="sm:w-auto"
          >
            Показать ещё
          </Button>
        </div>
      ) : null}

      <p className="border-t border-line px-4 py-2.5 text-xs text-fg-muted">
        Показано <span className="num">{items.length}</span> из{' '}
        <span className="num">{total}</span> {plural(total, NOUN.withdrawal)}.
      </p>
    </>
  );
}

function WithdrawalRow({ withdrawal }: { withdrawal: WithdrawalRequest }) {
  const isSent = withdrawal.status === WithdrawalStatus.Sent;

  return (
    <li className="px-4 py-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1.5">
        <span className="num text-md font-medium text-fg">{usdt(withdrawal.amountUsdt)}</span>
        <WithdrawalStatusBadge status={withdrawal.status} />
      </div>

      <dl className="mt-2 space-y-1.5">
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
          <dt className="text-fg-muted">Адрес</dt>
          <dd className="num min-w-0 flex-1 break-all text-fg-secondary">{withdrawal.address}</dd>
        </div>

        {withdrawal.txHash ? (
          <div className="flex flex-wrap items-start gap-x-3 gap-y-0.5 text-xs">
            <dt className="text-fg-muted">Хеш</dt>
            <dd className="flex min-w-0 flex-1 items-start gap-1">
              <span className="num min-w-0 break-all text-fg">{withdrawal.txHash}</span>
              <CopyButton value={withdrawal.txHash} label="хеш перевода" />
            </dd>
          </div>
        ) : null}

        {withdrawal.rejectionReason ? (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
            <dt className="text-fg-muted">Причина</dt>
            <dd className="min-w-0 flex-1 text-danger-500">{withdrawal.rejectionReason}</dd>
          </div>
        ) : null}
      </dl>

      <p className="mt-2 text-2xs text-fg-muted">
        <time dateTime={withdrawal.createdAt} title={absoluteDateTime(withdrawal.createdAt)}>
          {relativeTime(withdrawal.createdAt)}
        </time>
        {isSent && withdrawal.reviewedAt ? (
          <>
            {' · отправлен '}
            <time dateTime={withdrawal.reviewedAt}>{relativeTime(withdrawal.reviewedAt)}</time>
          </>
        ) : null}
      </p>
    </li>
  );
}
